import type { MensajePublic } from '@app/shared';
import { Bot, Smartphone } from 'lucide-react';

import { useI18n } from '../../../i18n/I18nProvider';
import { resolveApiUrl } from '../../../lib/api';
import { cn } from '../../../lib/cn';
import { horaCorta } from '../lib/formato';

// Una burbuja: entrantes a la izquierda, salientes a la derecha (como WhatsApp).
// Los salientes dicen de dónde salieron (app / celular / recontacto) para que la
// bandeja nunca mienta sobre quién escribió.
export function BurbujaMensaje({ mensaje }: { mensaje: MensajePublic }): JSX.Element {
  const es = useI18n();
  const saliente = mensaje.direccion === 'OUT';
  const esAudioReproducible = mensaje.tipo === 'AUDIO' && !!mensaje.audioUrl;
  const texto = mensaje.texto ?? es.bandeja.tipoRecibido[mensaje.tipo === 'TEXTO' ? 'OTRO' : mensaje.tipo];
  const esMedia = mensaje.tipo !== 'TEXTO';
  const origen = saliente ? es.bandeja.enviadoPor[mensaje.enviadoPor] : '';
  // ciclo-historial-fiel: como en WhatsApp. Lo borrado se ve como borrado (el texto
  // sigue a mano, tachado, para Ailén); lo editado muestra el texto nuevo y lo que
  // decía antes se ve al pasar el mouse por "editado".
  const eliminado = !!mensaje.eliminadoEn;
  const antes = mensaje.editadoEn && mensaje.textoOriginal ? mensaje.textoOriginal : null;
  // Edición cifrada (la hizo la otra persona): se sabe que cambió, no qué dice ahora.
  // El texto de la burbuja ya no es el actual, así que se avisa a la vista.
  const editadoSinTexto = !!mensaje.editadoEn && !mensaje.textoOriginal;

  return (
    <li className={cn('flex', saliente ? 'justify-end' : 'justify-start')}>
      <div
        className={cn(
          'max-w-[78%] rounded-2xl px-3 py-2 text-sm shadow-card',
          saliente
            ? 'rounded-br-sm bg-surface-primario text-text-strong'
            : 'rounded-bl-sm bg-surface text-text-strong',
          esMedia && !mensaje.texto && !esAudioReproducible && 'italic text-text-subtle',
        )}
      >
        {eliminado && (
          <p className="mb-1 text-xs italic text-text-subtle" data-testid="mensaje-eliminado">
            {es.bandeja.eliminado}
          </p>
        )}
        {editadoSinTexto && !eliminado && (
          <p className="mb-1 text-xs italic text-text-subtle" data-testid="mensaje-editado-cifrado">
            {es.bandeja.editadoSinTexto}
          </p>
        )}
        {esAudioReproducible ? (
          // eslint-disable-next-line jsx-a11y/media-has-caption -- es una nota de voz, no tiene pista de texto
          <audio controls src={resolveApiUrl(mensaje.audioUrl) ?? undefined} className="h-9 max-w-full" />
        ) : (
          <p className={cn('whitespace-pre-wrap break-words', eliminado && 'text-text-subtle line-through')}>
            {texto}
          </p>
        )}
        <p className="mt-1 flex items-center justify-end gap-1 text-[10px] text-text-faint">
          {saliente && mensaje.enviadoPor === 'TELEFONO' && <Smartphone size={10} aria-hidden />}
          {saliente &&
            (mensaje.enviadoPor === 'RECONTACTO_AUTO' || mensaje.enviadoPor === 'SISTEMA_APERTURA') && (
              <Bot size={10} aria-hidden />
            )}
          {antes && (
            <span title={`${es.bandeja.textoAntesDeEditar} ${antes}`} data-testid="mensaje-editado">
              {es.bandeja.editado} ·
            </span>
          )}
          {origen && <span>{origen} ·</span>}
          <time dateTime={mensaje.timestamp}>{horaCorta(mensaje.timestamp)}</time>
        </p>
      </div>
    </li>
  );
}
